// src/pages/NotificationsPage.js
import { format, formatDistanceToNow } from 'date-fns';
import React, { useEffect, useState } from "react";
import { useNavigate } from 'react-router-dom';
import { useNotification } from "../context/NotificationContext";
import axios from "../utils/axiosHandler";

const NotificationsPage = () => {
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [filter, setFilter] = useState("all"); // all, unread, read
  const { showNotification } = useNotification();
  const navigate = useNavigate();

  // Fetch notifications
  useEffect(() => {
    const fetchNotifications = async () => {
      try {
        const token = localStorage.getItem("token");
        if (!token) {
          setLoading(false);
          return;
        }

        const res = await axios.get(`/api/notifications/user?page=${page}&limit=10&filter=${filter}`, {
          headers: { Authorization: `Bearer ${token}` },
        });

        if (page === 1) {
          setNotifications(res.data.notifications || []);
        } else {
          setNotifications(prev => [...prev, ...(res.data.notifications || [])]);
        }

        // Check if there are more notifications to load
        setHasMore((res.data.total || 0) > (page * 10));
        setLoading(false);
      } catch (err) {
        console.error("Error fetching notifications:", err);
        showNotification("error", "Failed to load notifications");
        setLoading(false);
      }
    };

    fetchNotifications();
  }, [page, filter, showNotification]);

  // Load more notifications
  const loadMore = () => {
    if (!loading && hasMore) {
      setPage(prev => prev + 1);
    }
  };

  // Mark a notification as read
  const markAsRead = async (id) => {
    try {
      const token = localStorage.getItem("token");
      if (!token) return;

      await axios.put(
        `/api/notifications/${id}`,
        { isRead: true },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      // Update the local state
      setNotifications(prev =>
        prev.map(note =>
          note._id === id ? { ...note, isRead: true } : note
        )
      );
    } catch (err) {
      console.error("Error marking notification as read:", err);
      showNotification("error", "Failed to update notification");
    }
  };

  // Mark all notifications as read
  const markAllAsRead = async () => {
    try {
      const token = localStorage.getItem("token");
      if (!token) return;

      await axios.put(
        "/api/notifications/mark-all-read",
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );

      // Update the local state
      setNotifications(prev =>
        prev.map(note => ({ ...note, isRead: true }))
      );

      showNotification("success", "All notifications marked as read");
    } catch (err) {
      console.error("Error marking all notifications as read:", err);
      showNotification("error", "Failed to update notifications");
    }
  };

  // Handle notification click
  const handleNotificationClick = (notification) => {
    // Mark as read if not already
    if (!notification.isRead) {
      markAsRead(notification._id);
    }

    // If the notification has a link, navigate to it
    if (notification.link) {
      navigate(notification.link);
    }
  };

  // Format notification date
  const formatNotificationDate = (date) => {
    const notificationDate = new Date(date);
    const now = new Date();

    // If less than 24 hours ago, show relative time
    if (now - notificationDate < 24 * 60 * 60 * 1000) {
      return formatDistanceToNow(notificationDate, { addSuffix: true });
    }

    // Otherwise show the actual date
    return format(notificationDate, 'MMM d, yyyy • h:mm a');
  };

  // Get the notification icon based on type
  const getNotificationIcon = (type) => {
    switch (type) {
      case 'order':
        return (
          <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 text-blue-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 11V7a4 4 0 00-8 0v4M5 9h14l1 12H4L5 9z" />
          </svg>
        );
      case 'inventory':
        return (
          <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 text-green-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
          </svg>
        );
      case 'feedback':
        return (
          <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 text-yellow-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 10h.01M12 10h.01M16 10h.01M9 16H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-5l-5 5v-5z" />
          </svg>
        );
      case 'lead':
        return (
          <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 text-purple-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
          </svg>
        );
      case 'invoice':
        return (
          <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 text-indigo-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
          </svg>
        );
      default:
        return (
          <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 text-gray-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
          </svg>
        );
    }
  };

  return (
    <div className="min-h-screen max-w-5xl mx-auto py-8 px-4 sm:px-6 lg:px-8">
      <div className="flex justify-between items-center mb-8">
        <h1 className="text-3xl font-bold text-gray-900">Your Notifications</h1>

        <div className="flex space-x-4">
          {/* Filter buttons */}
          <div className="inline-flex rounded-md shadow-sm">
            <button
              type="button"
              onClick={() => setFilter("all")}
              className={`px-4 py-2 text-sm font-medium rounded-l-md ${filter === "all"
                ? "bg-blue-600 text-white"
                : "bg-white text-gray-700 hover:bg-gray-50"
                } border border-gray-300 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500`}
            >
              All
            </button>
            <button
              type="button"
              onClick={() => setFilter("unread")}
              className={`px-4 py-2 text-sm font-medium ${filter === "unread"
                ? "bg-blue-600 text-white"
                : "bg-white text-gray-700 hover:bg-gray-50"
                } border-t border-b border-r border-gray-300 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500`}
            >
              Unread
            </button>
            <button
              type="button"
              onClick={() => setFilter("read")}
              className={`px-4 py-2 text-sm font-medium rounded-r-md ${filter === "read"
                ? "bg-blue-600 text-white"
                : "bg-white text-gray-700 hover:bg-gray-50"
                } border-t border-b border-r border-gray-300 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500`}
            >
              Read
            </button>
          </div>

          {/* Mark all as read button */}
          <button
            onClick={markAllAsRead}
            className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
          >
            Mark all as read
          </button>
        </div>
      </div>

      <div className="bg-white shadow overflow-hidden sm:rounded-md">
        {loading && page === 1 ? (
          <div className="py-12 flex justify-center">
            <svg className="animate-spin h-8 w-8 text-blue-500" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"></path>
            </svg>
          </div>
        ) : notifications.length === 0 ? (
          <div className="py-24 flex flex-col items-center justify-center text-center">
            <svg xmlns="http://www.w3.org/2000/svg" className="h-16 w-16 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
            </svg>
            <h3 className="mt-6 text-xl font-medium text-gray-900">No notifications</h3>
            <p className="mt-2 text-sm text-gray-500">
              You don't have any notifications at the moment.
            </p>
          </div>
        ) : (
          <ul className="divide-y divide-gray-200">
            {notifications.map((notification) => (
              <li
                key={notification._id}
                className={`relative ${!notification.isRead ? 'bg-blue-50' : ''} hover:bg-gray-50 cursor-pointer transition duration-150 ease-in-out`}
                onClick={() => handleNotificationClick(notification)}
              >
                <div className="px-4 py-4 sm:px-6">
                  <div className="flex items-start">
                    {/* Notification icon */}
                    <div className="flex-shrink-0 mr-4">
                      {getNotificationIcon(notification.type)}
                    </div>

                    {/* Content */}
                    <div className="flex-1 min-w-0">
                      {/* Unread indicator */}
                      {!notification.isRead && (
                        <span className="absolute inset-0 rounded-md pointer-events-none" aria-hidden="true">
                          <span className="absolute right-4 top-5 block h-3 w-3 rounded-full bg-blue-500"></span>
                        </span>
                      )}

                      {/* Message */}
                      <p className={`text-sm font-medium ${!notification.isRead ? 'text-blue-900' : 'text-gray-800'}`}>
                        {notification.message}
                      </p>

                      {/* Metadata */}
                      <div className="mt-1 flex items-center text-xs text-gray-500">
                        <span className="capitalize">{notification.type}</span>
                        <span className="mx-1">•</span>
                        <time dateTime={notification.createdAt}>
                          {formatNotificationDate(notification.createdAt)}
                        </time>
                      </div>
                    </div>

                    {/* Chevron icon */}
                    <div className="ml-5 flex-shrink-0">
                      <svg className="h-5 w-5 text-gray-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                        <path fillRule="evenodd" d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z" clipRule="evenodd" />
                      </svg>
                    </div>
                  </div>
                </div>
              </li>
            ))}
          </ul>
        )}

        {/* Load more button */}
        {hasMore && (
          <div className="px-4 py-4 sm:px-6 text-center">
            <button
              onClick={loadMore}
              disabled={loading}
              className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-blue-700 bg-blue-100 hover:bg-blue-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
            >
              {loading ? (
                <>
                  <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-blue-700" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"></path>
                  </svg>
                  Loading...
                </>
              ) : (
                "Load more"
              )}
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default NotificationsPage;